import { initialize } from "./initialize";
import { store } from "store";

const UST_ID = process.env.UST_ID ? process.env.UST_ID : "";

initialize();

export interface Event {
    category: string;
    action: string;
    label?: string;
}

// NOTE!
// When it comes to adding or removing, we'll always use "add" and "remove".
// Not for example, "create" or "delete". That applies equally to the enum
// names and their values. Always "ADD" in the enum name and obviously "add"
// in the values because they're always in lowercase.
// Also, "new" isn't used in the value when adding something. It's just "add
// user" and not "add new user".

export enum Action {
    // Header actions
    CHANGE_LANGUAGE = "change language",
    CHANGE_USER_THEME = "change user theme",
    DOWNLOAD_TERMS_AND_CONDITIONS = "download terms and conditions",
    LOGOUT = "logout",
    REMOVE_NOTIFICATION = "remove notification",
    VIEW_TERMS_AND_CONDITIONS = "view terms and conditions",
    VIEW_USER_PROFILE = "view user profile",
    VIEW_USER_SETTINGS = "view user settings",
    VISIT_SYSTEM_STATUS = "visit system status",
    VISIT_TRUST_CENTER = "visit trust center",
    VIEW_NEWS_ARTICLE = "view news article",

    // License actions
    ADD_LICENSE_CONFIGURATION = "add license configuration",
    ASSIGN_LICENSE_KEY = "assign license keys",
    UPDATE_LICENSE_KEY = "update license keys",
    ASSIGN_OEM_LICENSE_KEY = "assign OEM license keys",
    CHANGE_LICENSE_KEY_ASSIGNED_COUNT = "change license key assigned count",
    CHANGE_LICENSE_KEY_END_DATE = "change license key end date",
    CHANGE_LICENSE_KEY_IDENTIFIER = "change license key identifier",
    CHANGE_LICENSE_KEY_START_DATE = "change license key start date",
    CHANGE_LICENSE_KEY_USAGE_HOURS = "change license key usage hours",
    CHANGE_NUMBER_OF_LICENSE_KEYS = "change number of license keys",
    EDIT_LICENSE_CONFIGURATION = "edit license configuration",
    EXPORT_LICENSE_KEYS = "export license keys",
    VIEW_LICENSE_CONFIGURATION = "view license configuration",
    UPLOAD_KEYS_DRAGGING = "upload license keys via dragging",
    UPLOAD_KEYS_SELECTING = "upload license keys via selecting",
    DELETE_LICENSE_KEYS = "delete license keys",
    DELETE_SINGLE_LICENSE_KEY = "delete license key",

    // Report actions
    SEARCH_DEVICE_PASSPORT = "search device passport",
    PRINT_DEVICE_PASSPORT = "print device passport",
    CREATE_DEVICE_PASSPORT_LINK = "create device passport link",
    PRINT_DEVICE_PASSPORT_ARTIFACT = "print device passport artifact",
    CHANGE_COLUMN_SORT = "change column sort",
    CHANGE_COLUMN_WIDTH = "change column width",
    EXECUTE_QUICK_SEARCH = "execute quick search",
    EXPORT_DIAGNOSTIC_REPORT = "export diagnostic report",
    EXPORT_SELECTED_REPORTS = "export selected reports",
    EXPORT_REPORTS_WITH_PUBLIC_LINKS = "export reports with public links",
    LAUNCH_REPORT_IMPORT = "launch report import",
    LOAD_MORE = "load more",
    PREVIEW_ERASURE_REPORT_WITH_TEMPLATE = "preview erasure report with template",
    PRINT_SELECTED_REPORTS = "print selected reports",
    SHARE_SELECTED_REPORTS = "share selected reports",
    SHARE_SINGLE_REPORT = "share a single report",
    REMOVE_SINGLE_REPORT = "remove a single report",
    EDIT_SINGLE_REPORT = "edit a single report",
    REMOVE_MULTIPLE_REPORTS = "remove multiple reports",
    VIEW_DEVICE_DETAILS = "view device details",
    VIEW_DIAGNOSTIC_BUYBACK_TRADEIN = "view buy-back / trade-in",
    VIEW_DIAGNOSTIC_SUMMARY = "view diagnostic summary",
    VIEW_INSURANCE = "view insurance",
    VIEW_NTF = "view ntf",
    VIEW_REPORT_PROPERTIES = "view report properties",
    VIEW_SCREEN_ANALYSIS = "view screen analysis",
    VIEW_DATE_RANGE_FILTER = "view date range filter",
    SELECT_DATE_RANGE = "select date range filter",
    UPLOAD_REPORTS_DRAGGING = "upload reports via dragging",
    UPLOAD_REPORTS_SELECTING = "upload reports via selecting",
    SELECT_QUICK_FILTER = "select quick filter",
    RECHECK_SINGLE_REPORT = "recheck a single report",
    RECHECK_MULTIPLE_REPORTS = "recheck multiple reports",

    // Report import notification actions
    SEE_DETAILS = "see details",

    // Report import results dialog
    REFRESH = "refresh",

    // Tenant actions
    ACCESS_TENANT = "access tenant",
    ADD_TENANT = "add tenant",
    ADD_SUB_TENANT = "add sub-tenant",
    EDIT_TENANT = "edit tenant",
    ENABLE_TENANT = "enable tenant",
    UPLOAD_CUSTOMER_LOGO = "upload customer logo",
    VIEW_TENANT_DETAILS = "view tenant details",
    VIEW_TENANT_HIERARCHY = "view tenant hierarchy",
    DELETE_TENANT = "delete tenant",
    EDIT_MC_CONFIGURATION = "edit mc configuration",
    MOVE_TENANT = "move tenant",

    // User actions
    ADD_USER = "add user",
    EDIT_USER = "edit user",
    REMOVE_USER = "remove user",
    ENABLE_USER = "enable user",

    // API key actions
    ADD_API_KEY = "add api key",
    CHANGE_API_KEY_EXPIRATION = "change api key expiration",
    CHANGE_API_KEY_SCOPE = "change api key scope",
    EDIT_API_KEY = "edit api key",
    REMOVE_API_KEY = "remove api key",
    REMOVE_API_KEY_EXPIRATION = "remove api key expiration",

    // Workflow templates actions
    ADD_WORKFLOW_TEMPLATE = "add workflow template",
    EXPORT_WORKFLOW_TEMPLATE = "export workflow template",
    PUBLISH_TEMPLATE = "publish workflow template",
    REMOVE_WORKFLOW_TEMPLATE = "remove workflow template",
    ADD_SUB_WORKFLOW_TEMPLATE = "add sub-workflow",
    UNPUBLISH_TEMPLATE = "unpublish workflow template",

    // Workflow actions
    ADD_WORKFLOW = "add workflow",
    CLOSE_WORKFLOW_UPDATES_DIALOG = "close workflow updates dialog",
    CREATE_SCHEDULE = "create schedule",
    DELETE_SCHEDULE = "delete schedule",
    EXPORT_ENCRYPTED = "Export encrypted",
    EXPORT_WORKFLOW = "export workflow",
    FETCH_EXECUTION_LOGS = "fetch execution logs",
    IMPORT_WORKFLOW = "import workflow",
    LAUNCH_WORKFLOW = "launch workflow",
    LAUNCH_WORKFLOW_INITIATED = "launch workflow initiated",
    LOAD_WORKFLOW_VERSION = "load workflow version",
    OPEN_OLD_WORKFLOW_IN_NEW_EDITOR = "open the old workflow in the new editor",
    REFRESH_QUOTAS = "refresh quotas",
    REFRESH_WORKFLOW_STATES = "refresh workflow states",
    REMOVE_WORKFLOW = "remove workflow",
    RESTORE_WORKFLOW_VERSION = "restore workflow version",
    RETRY_WORKFLOW = "retry workflow",
    SAVE_WORKFLOW_AS = "save workflow as",
    SET_DEFAULT_WORKFLOW = "set default workflow",
    SHOW_USER_LIST_IN_WORKFLOW = "show active user list",
    UPDATE_WORKFLOW_INITIATED = "update workflow initiated",
    UPDATE_WORKFLOW_POSTPONED = "update workflow postponed",
    UPDATE_WORKFLOW_TEMPLATE_INITIATED = "update workflow template initiated",
    VIEW_VERSION_HISTORY = "view version history",

    // Entitlement actions
    ADD_ENTITLEMENT = "add entitlement",
    ADD_ENTITLEMENT_LICENSE = "add entitlement license",
    CHANGE_ENTITLEMENT_CONTAINER = "change entitlement container",
    CHANGE_ENTITLEMENT_TYPE = "change entitlement type",
    CHANGE_LICENSE_TYPE = "change license type",
    EDIT_CONTAINER = "edit container",
    EDIT_ENTITLEMENT = "edit entitlement",
    REMOVE_ALL_LICENSES = "clear licenses",
    REMOVE_LICENSE = "remove license",
    SYNCHRONIZE_ENTITLEMENTS = "synchronize entitlements",

    // License delivery actions
    ADD_LICENSE_DELIVERY = "add license delivery",
    ADD_LICENSE_TO_DELIVERY = "add license to delivery",
    CHANGE_DELIVERY_TYPE = "change delivery type",
    EDIT_LICENSE_DELIVERY = "edit license delivery",
    MANUALLY_TRIGGERED_LICENSE_DELIVERY = "triggered manually license delivery",
    VIEW_LICENSE_DELIVERY = "view license delivery",

    // License events actions
    EXPORT_LICENSE_EVENTS = "export license history",
    FILTER_BY_ACTION = "select event action",
    FILTER_BY_DATE = "select date range",
    FILTER_BY_PRODUCT = "select product",
    FILTER_BY_TYPE = "select event type",
    FILTER_BY_EVENT_SOURCE = "select event source",

    // Token rate actions
    CHANGE_TOKEN_RATE_VERSION = "change token rate version",

    // Report template actions
    ADD_REPORT_TEMPLATE = "add report template",
    EDIT_REPORT_TEMPLATE = "edit report template",
    REMOVE_REPORT_TEMPLATE = "remove report template",
    SET_DEFAULT_REPORT_TEMPLATE = "set default report template",
    VIEW_REPORT_TEMPLATE = "view report template",

    // Report signature key actions
    ADD_REPORT_SIGNATURE_KEY = "add report signature key",
    RENAME_REPORT_SIGNATURE_KEY = "rename report signature key",
    REMOVE_REPORT_SIGNATURE_KEY = "remove report signature key",

    // Report view actions
    ADD_COLUMN = "add column",
    ADD_FILTER = "add filter",
    ADD_FILTER_GROUP = "add filter group",
    ADD_REPORT_VIEW = "add report view",
    CHANGE_FILTER_OPERATOR = "change filter operator",
    CHANGE_FILTER_PATH = "change filter path",
    CHANGE_PRIVATE = "change private",
    CHANGE_PRODUCT = "change product",
    CLONE_REPORT_VIEW = "clone report view",
    EDIT_REPORT_VIEW = "edit report view",
    EXPORT_REPORT_VIEW = "export report view",
    IMPORT_REPORT_VIEW = "import report view",
    REMOVE_COLUMN = "remove column",
    REMOVE_FILTER = "remove filter",
    REMOVE_FILTER_GROUP = "remove filter group",
    REMOVE_REPORT_VIEW = "remove report view",

    // Dashboard view actions
    CALCULATE_ESG_VIEW = "calculate esg view",

    // Role actions
    ADD_ROLE = "add role",
    VIEW_ROLE_DETAILS = "view role details",
    EDIT_ROLE = "edit role",
    SET_DEFAULT_ROLE = "set default role",
    CLONE_ROLE = "clone role",
    DELETE_ROLE = "delete role",
    EXPORT_ROLE = "export role",
    IMPORT_ROLE = "import role",

    // Report paths actions
    EXPORT_REPORT_PATHS = "export report paths",

    // User group actions
    REMOVE_USER_GROUP = "remove user group",
    VIEW_USER_GROUP_DETAILS = "view user group details",

    // License pool actions
    ADD_LICENSE_TO_POOL = "add license to pool",
    DELETE_LICENSE_POOL = "delete a license pool",
    MOVE_LICENSES_BETWEEN_POOLS = "move licenses between pools",

    // License alerts actions
    DELETE_LICENSE_ALERTS = "delete license alerts",
    EDIT_LICENSE_ALERTS = "edit license alerts",
    VIEW_LICENSE_ALERTS = "view license alerts",

    // MCs
    EDIT_MC = "edit mc",
    UNREGISTER_MC = "unregister mc",
    RESET_MC = "reset mc",
    REGISTER_MC = "register mc",
    DISABLE_MC = "disable mc",
    ENABLE_MC = "enable mc",
    REGISTER_OFFLINE = "register and sync offline",
    SYNC_OFFLINE = "sync offline",

    // Onboarding
    CREATE_CUSTOM_VIEW = "create custom views",
    CREATE_WORKFLOW = "create workflow",
    INVITE_USER = "invite user",
    GET_STARTED = "get started",
    CLOSE_CUSTOMER_SURVEY = "close customer survey",

    // Trial Onboarding Video
    WATCH_TRIAL_ONBOARDING_VIDEO = "watch trial onboarding video",
    CLOSE_TRIAL_ONBOARDING_VIDEO = "close trial onboarding video",

    // Asset management actions
    ADD_ASSET = "add asset",
    ADD_ASSET_FROM_REPORT = "add asset from report",
    EDIT_ASSET = "edit asset",
    REMOVE_ASSET = "remove asset",
    REMOVE_ASSET_VIEW = "remove asset view",
    UPLOAD_ASSETS_FILE_DRAGGING = "upload assets file via dragging",
    UPLOAD_ASSETS_FILE_SELECTING = "upload assets file via selected",

    // File sharing actions
    DOWNLOAD_FILE = "download file",
    MOVE_FILE = "move file",
    REMOVE_FILE = "remove file",
    RENAME_FILE = "rename file",
    UPLOAD_FILE = "upload file",

    // Audit log actions
    VIEW_AUDIT_LOGS = "view audit logs",
    DELETE_AUDIT_LOGS = "delete audit logs",
    EXPORT_AUDIT_LOGS = "export audit logs",
    VIEW_DELETION_REPORTS = "view deletion reports",
    DOWNLOAD_DELETION_REPORT = "download deletion report",

    // AI actions
    SEND_MESSAGE = "send message",
    OPEN_PANEL = "open panel",
    CLOSE_PANEL = "close panel",
    MAXIMIZE_PANEL = "maximize panel",
    MINIMIZE_PANEL = "minimize panel",
}

export enum Category {
    API_KEYS = "api keys",
    AI_AGENT = "ai agent",
    ASSET_MANAGEMENT = "asset management",
    FILE_SHARING = "file sharing",
    ASSET_VIEW_MANAGEMENT = "asset view management",
    DASHBOARD = "dashboard",
    HEADER = "header",
    LICENSE = "license",
    LICENSE_KEYS = "license keys",
    LICENSE_DELIVERY = "license delivery",
    LICENSE_ENTITLEMENT = "license entitlement",
    LICENSE_EVENTS = "license history",
    LICENSE_POOLS = "license pool",
    LICENSE_ALERTS = "license alerts",
    TOKEN_RATE = "license rate",
    NEW_WORKFLOW_EDITOR = "new workflow editor",
    REPORTS = "reports",
    DEVICE_PASSPORT = "device passport",
    REPORT_IMPORT = "report import",
    REPORT_DELETION = "report deletion",
    REPORT_TEMPLATE = "report template",
    REPORT_SIGNATURE_KEY = "report signature key",
    REPORT_VIEW = "report view",
    TENANT = "tenant",
    USER = "user",
    WORKFLOW = "workflow",
    ROLE = "role",
    REPORT_PATHS = "report paths",
    USER_GROUP = "user group",
    ADVANCED_SEARCH = "advanced search",
    WORKFLOW_TEMPLATES = "workflow templates",
    AUDIT_LOGS = "audit logs",
    MCS = "mcs",
    ONBOARDING = "onboarding",
    SURVEY = "survey",
    TRIAL_ONBOARDING_VIDEO = "trial onboarding video",
}

export enum Label {
    ALL = "all",
    ERASURE = "erasure",
    DIAGNOSTIC = "diagnostic",
    ENTITLEMENT = "entitlement",
    CONTAINER = "container",
    BMS = "bms",
    SERIAL_NUMBER_KEYS = "serial number keys",
    LICENSE = "license",
    LICENSE_DELIVERY = "license_delivery",
    USER = "user",
    TODAY = "today",
    YESTERDAY = "yesterday",
    THIS_WEEK = "this week",
    LAST_SEVEN_DAYS = "last seven days",
    THIS_MONTH = "this month",
    LAST_MONTH = "last month",
    LAST_THIRTY_DAYS = "last 30 days",
    THIS_YEAR = "this year",
    LAST_YEAR = "last year",
    CUSTOM = "custom",
    REPORT_PATHS = "report paths",
    USER_GROUP_DETAILS_DIALOG = "user group details dialog",
    LICENSE_ALERT = "license alerts",
    WORKFLOW_TEMPLATES = "Workflow templates",
    WORKFLOW_SIDEPANEL = "Workflow sidepanel",
    BDE = "BDE",
    BMDE = "BMDE",
    BMPOP = "BMPOP",
    BAR = "BAR",
    BMWE = "BMWE",
}

class UsageStatisticsService {
    public static isEnabled(): boolean {
        const storeElement = store.getState();
        let usageStatistics = true;
        if (storeElement.userReducer.user !== null) {
            usageStatistics = storeElement.userReducer.user.usageStatistics;
        }
        return usageStatistics;
    }

    public sendEvent(event: Event): void {
        if (UsageStatisticsService.isEnabled() && UST_ID !== "") {
            if (event.label === Action.CHANGE_LANGUAGE) {
                gtag("set", "language", event.label);
            }
            gtag("event", event.category, {
                action: event.action,
                ...(event.label != null ? { label: event.label } : {}),
            });
        }
    }
}

export const usageStatisticsService = new UsageStatisticsService();
